import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "../../api/axios";
import heroImg from "../../assets/hero-ayurvedic.png";
import { motion, AnimatePresence } from "framer-motion";

const Hero = () => {
  document.title = "HOLIVANA - Ayurvedic Medicines";
  const [banner, setBanner] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBanner();
  }, []);

  const fetchBanner = async () => {
    try {
      const response = await axios.get('/cms/banners');
      const heroBanner = response.data.find(b => b.position === 'hero');
      setBanner(heroBanner);
    } catch (error) {
      console.error('Error fetching banner:', error);
    } finally {
      setLoading(false);
    }
  };

  // Use banner data if available, otherwise use defaults
  const title = banner?.title || "Pure Ayurveda, Crafted by Nature";
  const subtitle = banner?.subtitle || "Experience the power of traditional Ayurvedic medicine crafted with care by HOLIVANA. 100% natural, sugar-free formulations for your wellness journey.";
  const backgroundImage = banner?.imageUrl || heroImg;
  const buttonText = banner?.buttonText || "Shop Now";
  const linkUrl = banner?.linkUrl || "/products";

  return (
    <AnimatePresence>
      <section className="relative overflow-hidden h-screen min-h-[600px]">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${backgroundImage})` }}
        >
          {/* Overlay for better text readability */}
          <div className="absolute inset-0 bg-black/30"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 h-full px-5 flex items-center lg:mx-20 xl:mx-28 2xl:mx-36 3xl:mx-auto lg:px-0 xl:px-3 max-w-xl md:max-w-2xl mx-auto lg:max-w-7xl">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-left w-full lg:w-1/2 text-white"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg mb-6 border border-orange/20"
            >
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              <span className="text-sm text-dark-grayish-blue font-medium">
                Trusted by 10,000+ customers
              </span>
            </motion.div>

            {/* Main Heading */}
            <h1 className="font-bold text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-tight mb-6 text-white drop-shadow-2xl">
              {title.split(',')[0]},
              <br />
              <span className="text-orange relative inline-block">
                {title.split(',')[1]?.trim().split(' ')[0] || 'Crafted'}
                <svg
                  className="absolute -bottom-2 left-0 w-full"
                  height="12"
                  viewBox="0 0 200 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2 10C50 2 150 2 198 10"
                    stroke="hsl(142, 76%, 36%)"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
              </span>{" "}
              {title.split(',')[1]?.trim().split(' ').slice(1).join(' ') || 'by Nature'}
            </h1>

            {/* Subheading */}
            <p className="text-white/90 text-base sm:text-lg lg:text-xl leading-relaxed mb-8 max-w-xl drop-shadow-lg">
              {subtitle}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to={linkUrl} className="w-full sm:w-auto">
                <button className="w-full sm:w-auto px-8 py-4 bg-orange text-white text-base font-semibold rounded-xl shadow-lg hover:shadow-xl hover:bg-orange/90 transition-all duration-300 transform hover:-translate-y-0.5">
                  {buttonText}
                  <span className="ml-2">→</span>
                </button>
              </Link>
              <Link to="collections" className="w-full sm:w-auto">
                <button className="w-full sm:w-auto px-8 py-4 bg-white/90 backdrop-blur-sm text-orange text-base font-semibold rounded-xl border-2 border-orange hover:bg-orange hover:text-white transition-all duration-300">
                  View Collections
                </button>
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center gap-6 mt-10 text-sm text-white/90">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="drop-shadow">100% Authentic</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="drop-shadow">Fast Delivery</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="drop-shadow">Best Prices</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </AnimatePresence>
  );
};

export default Hero;
